/**
 * RejectionDialog Component
 *
 * Shared rejection dialog for approval workflows across the application.
 * Consolidates 3 duplicate ApprovalDialog components into one reusable component.
 *
 * Used in:
 * - Waste Orders Report (reject order)
 * - School Requests Report (reject registration)
 * - Lesson Answers Report (reject submission)
 *
 * Created: 2025-11-02 (Phase 5 - Admin Forms Refactoring)
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useRejectionDialog } from '@/shared/hooks/useRejectionDialog';

/**
 * Component Props
 */
interface RejectionDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;

  /**
   * Callback when dialog should close
   */
  onClose: () => void;

  /**
   * Callback when rejection is submitted
   * Receives the rejection reason string
   */
  onReject: (reason: string) => Promise<void>;

  /**
   * Whether the rejection operation is in progress
   */
  loading?: boolean;

  /**
   * Title for the dialog
   * @default "Respingere"
   */
  title?: string;

  /**
   * Description for the dialog (optional)
   * Shown below the title
   */
  description?: string;

  /**
   * Context information to display (optional)
   * e.g., Order number, school name, submission details
   * @example
   * ```tsx
   * contextInfo={[
   *   { label: 'Comandă', value: '#12345' },
   *   { label: 'Școală', value: 'Școala ABC' }
   * ]}
   * ```
   */
  contextInfo?: Array<{
    label: string;
    value: string;
  }>;

  /**
   * Warning message to display
   * @default "Atenție: Această acțiune nu poate fi anulată."
   */
  warningMessage?: string;

  /**
   * Text for the reject button
   * @default "Respinge"
   */
  rejectButtonText?: string;

  /**
   * Text for the cancel button
   * @default "Anulare"
   */
  cancelButtonText?: string;
}

/**
 * RejectionDialog Component
 *
 * Displays a dialog for rejecting orders, registrations, or submissions.
 * Includes form validation, context information, and warning message.
 *
 * @example
 * ```tsx
 * <RejectionDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onReject={async (reason) => {
 *     await api.rejectOrder(orderId, reason);
 *     toast.success('Comandă respinsă');
 *   }}
 *   loading={mutation.isPending}
 *   title="Respingere Comandă"
 *   contextInfo={[
 *     { label: 'Comandă', value: `#${orderNumber}` },
 *     { label: 'Școală', value: schoolName }
 *   ]}
 * />
 * ```
 */
export function RejectionDialog({
  isOpen,
  onClose,
  onReject,
  loading = false,
  title = 'Respingere',
  description,
  contextInfo,
  warningMessage = 'Atenție: Această acțiune nu poate fi anulată.',
  rejectButtonText = 'Respinge',
  cancelButtonText = 'Anulare',
}: RejectionDialogProps) {
  const { form, onSubmit, resetForm } = useRejectionDialog({
    onReject,
    loading,
    onClose,
  });

  /**
   * Handle dialog close
   * Resets form state
   */
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4">
            {/* Context Information */}
            {contextInfo && contextInfo.length > 0 && (
              <div className="rounded-md bg-gray-50 p-3">
                {contextInfo.map((info, index) => (
                  <p key={index} className="text-sm text-gray-700">
                    <span className="font-semibold">{info.label}:</span> {info.value}
                  </p>
                ))}
              </div>
            )}

            {/* Rejection Reason Field */}
            <FormField
              control={form.control}
              name="rejectReason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="form-label-bold">Motiv respingere *</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      disabled={loading}
                      rows={4}
                      placeholder="Introduceți motivul pentru care respingeți această cerere..."
                      className="resize-none"
                    />
                  </FormControl>
                  <FormMessage className="form-message-spacer" />
                </FormItem>
              )}
            />

            {/* Warning Message */}
            <div className="lesson-warning-box">
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">Atenție:</span> {warningMessage}
              </p>
            </div>

            {/* Actions */}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                {cancelButtonText}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={loading}
              >
                {loading ? 'Se procesează...' : rejectButtonText}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
